import React, { FC, useState, useCallback } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { targetLinkdsk } from '../../../../utils/targetDskLink';
import MediaElement from '../../../@shared/configurationCardMediaElement';
import Icon from '../../../@shared/icon';
import ModalDialog from '../../../@shared/modalDialog';
import OverlayCarouselPopup from '../../../@shared/overlayCarouselPopup';
import { TGalleryItem } from '../../../@shared/overlayCarouselPopup/types/overlayCarouselPopup.types';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import { ConfigurationCardModel } from '../../models/productModelConfigurationCard.model';
import { TCHModelConfigurationInnerComponent } from '../../types/productModelConfigurations.types';
import { appendRootToRichTextInternalLinks } from '../../../../utils/appendRootToRichTextInternalLinks.utils';
import { appendRootToInternalLink } from '../../../../utils/appendRootToInternalLink.utils';

const ModelConfigurationCard: FC<TCHModelConfigurationInnerComponent> = (props) => {
  const model = new ConfigurationCardModel(props);

  const [showOverlay, setShowOverlay] = useState(false);

  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const closeOverlayModal = (): void => setShowOverlay(false);

  const showOverlayVideo = (): void => {
    if (!model.isVideo) return;
    setShowOverlay(true);
  };

  const getOverlayProps: () => TGalleryItem[] = useCallback(() => {
    const externalThumbnailUrl = model.getExternalThumbnail;
    return [
      {
        desktopRendition: externalThumbnailUrl ? { url: externalThumbnailUrl } : model.firstAsset?.rendition_desktop_size500,
        mobileRendition: externalThumbnailUrl ? { url: externalThumbnailUrl } : model.firstAsset?.rendition_mobile_size500,
        desktopAlt: model.firstAsset?.alt,
        mobileAlt: model.firstAsset?.alt,
        isVideo: model.isVideo,
        videoUrl: model.getVideoUrl,
      },
    ];
  }, [model.assets]); // eslint-disable-line

  return (
    <article className="model-configuration-card">
      <div className="model-configuration-card__content">
        {model.assets?.length > 0 && (
          <div
            className={`model-configuration-card__image-wrapper ${
              model.getVideoUrl ? 'model-configuration-card__article-picture--fixed-aspect-ratio' : ''
            }`}
          >
            <MediaElement model={model} pictureClassName="model-configuration-card__article-picture--fixed-aspect-ratio" />
            {model.getVideoUrl && (
              <div onClick={showOverlayVideo} className="model-configuration-card__play-icon-wrapper">
                <Icon iconName="action-play" extraClassName="model-configuration-card__play-icon" size={isWide ? 32 : 24} />
              </div>
            )}
          </div>
        )}
        <RichText tag="h3" field={{ value: model.title }} className="model-configuration-card__title rich-text" />
        <div
          className="model-configuration-card__description rich-text"
          dangerouslySetInnerHTML={{ __html: appendRootToRichTextInternalLinks(model.description) || '' }}
        />
      </div>

      {((model.button1 && model.button1.url) || (model.button2 && model.button2.url)) && (
        <div className="model-configuration-card__buttons">
          {model.button1 && model.button1.url && (
            <RoutableNotEditableLink
              className="model-configuration-card__buttons--primary"
              href={appendRootToInternalLink(model.button1.url)}
              target={targetLinkdsk({ value: { href: model.button1?.url, target: model.button1?.target } }, isWide)}
            >
              {model.button1.label}
            </RoutableNotEditableLink>
          )}
          {model.button2 && model.button2.url && (
            <span className="model-configuration-card__buttons--secondary">
              <RoutableNotEditableLink
                href={appendRootToInternalLink(model.button2.url)}
                target={targetLinkdsk({ value: { href: model.button2?.url, target: model.button2?.target } }, isWide)}
              >
                {model.button2.label}
              </RoutableNotEditableLink>
              <Icon iconName="arrow-chevron-right" size={24} extraClassName="model-configuration-card__buttons--icon" />
            </span>
          )}
        </div>
      )}
      {model.getVideoUrl && (
        <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
          <OverlayCarouselPopup slides={getOverlayProps()} isEE={false} />
        </ModalDialog>
      )}
    </article>
  );
};

export default ModelConfigurationCard;
